import {
  Avatar,
  AvatarStack,
  BlockedBadge,
  EmptyState,
  ErrorNotice,
  PriorityBadge,
  Spinner,
  StatusDot,
  TypeBadge,
} from '@/components/ui';
import { PeoplePicker } from '@/components/PeoplePicker';
import { TaskDrawer } from '@/components/TaskDrawer';
import { useAddProjectMember, useBoard, useMoveItem, useProject } from '@/hooks/queries';
import { STATUS_META } from '@/lib/design';
import { useAuth } from '@/stores/auth';
import { useProjectStore } from '@/stores/project';
import type { BacklogItem, ItemStatus } from '@/types/api';
import { useState } from 'react';

type Filter = 'todas' | 'mias' | 'bloqueadas';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'todas', label: 'Todas' },
  { id: 'mias', label: 'Mis tareas' },
  { id: 'bloqueadas', label: 'Bloqueadas' },
];

export function BoardPage() {
  const projectId = useProjectStore((state) => state.activeProjectId);
  const currentUser = useAuth((state) => state.user);
  const { data: board, isLoading, error } = useBoard(projectId);
  const { data: project } = useProject(projectId);
  const moveItem = useMoveItem(projectId);

  const [filter, setFilter] = useState<Filter>('todas');
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<ItemStatus | null>(null);
  const [addingPeople, setAddingPeople] = useState(false);
  const addMember = useAddProjectMember(projectId);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5">
        <ErrorNotice>{(error as Error).message}</ErrorNotice>
      </div>
    );
  }

  if (!board?.sprint) {
    return (
      <div className="p-6">
        <EmptyState
          title="No hay ningun sprint en curso"
          hint="Crea un sprint desde el backlog y arrancalo para llenar el tablero."
        />
      </div>
    );
  }

  const matches = (item: BacklogItem) => {
    if (filter === 'mias') {
      return item.assignee?.id === currentUser?.id;
    }
    if (filter === 'bloqueadas') {
      return item.isBlocked;
    }
    return true;
  };

  const columns = board.columns.map((column) => ({
    ...column,
    items: column.items.filter(matches),
  }));

  const visibleCount = columns.reduce((sum, c) => sum + c.items.length, 0);
  const pointsTotal = board.columns.reduce((sum, c) => sum + c.storyPoints, 0);
  const pointsDone = board.columns.find((c) => c.status === 'DONE')?.storyPoints ?? 0;
  const members = project?.members.map((m) => m.user) ?? [];

  function handleDrop(status: ItemStatus, itemId: string) {
    setDragOverColumn(null);
    const current = board?.columns.find((c) => c.items.some((i) => i.id === itemId));
    if (current?.status === status) {
      return;
    }
    moveItem.mutate({ itemId, status });
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-12 flex-none items-center gap-2.5 border-b border-line bg-surface px-5">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            aria-pressed={filter === f.id}
            className={`rounded-control px-3 py-[5px] text-[12.5px] transition-colors ${
              filter === f.id
                ? 'bg-brand font-semibold text-brand-on'
                : 'border border-line-strong bg-surface text-muted hover:border-line-hover'
            }`}
          >
            {f.label}
          </button>
        ))}

        <span aria-hidden="true" className="mx-1 h-5 w-px bg-line" />
        <AvatarStack users={members} max={5} />
        {project?.canManage && (
          <button
            type="button"
            onClick={() => setAddingPeople(true)}
            title="Agregar personas al proyecto"
            aria-label="Agregar personas al proyecto"
            className="flex size-[26px] flex-none items-center justify-center rounded-full border border-dashed border-line-dashed text-[14px] leading-none text-subtle transition-colors hover:border-brand hover:text-brand-deep"
          >
            +
          </button>
        )}

        <div className="flex-1" />
        <p className="text-[12.5px] text-subtle">
          {visibleCount} {visibleCount === 1 ? 'tarea' : 'tareas'} · {pointsDone}/{pointsTotal} puntos
        </p>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-4 gap-3.5 overflow-auto px-5 py-4">
        {columns.map((column) => (
          <section
            key={column.status}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOverColumn(column.status);
            }}
            onDragLeave={() => setDragOverColumn((s) => (s === column.status ? null : s))}
            onDrop={(e) => {
              e.preventDefault();
              const itemId = e.dataTransfer.getData('text/plain');
              if (itemId) {
                handleDrop(column.status, itemId);
              }
            }}
            className={`flex min-h-0 flex-col gap-2.5 rounded-panel transition-colors ${
              dragOverColumn === column.status ? 'bg-brand-tint' : ''
            }`}
          >
            <header className="flex items-center gap-2 px-0.5">
              <StatusDot status={column.status} />
              <h2 className="text-xs font-semibold uppercase tracking-[.06em]">
                {STATUS_META[column.status].label}
              </h2>
              <span className="rounded-[9px] bg-chip px-[7px] py-px text-[11.5px] font-semibold text-faint">
                {column.items.length}
              </span>
            </header>

            <div className="flex flex-1 flex-col gap-2.5 overflow-auto pb-1.5">
              {column.items.map((item) => (
                <TaskCard key={item.id} item={item} onOpen={() => setOpenItemId(item.id)} />
              ))}

              {column.items.length === 0 && (
                <p className="px-1 py-3 text-center text-xs text-faint">Nada por aqui</p>
              )}
            </div>
          </section>
        ))}
      </div>

      {openItemId && <TaskDrawer itemId={openItemId} onClose={() => setOpenItemId(null)} />}

      {addingPeople && (
        <PeoplePicker
          title="Agregar al sprint"
          description="Se suma al proyecto: vera este tablero, el backlog y el canal de chat."
          confirmLabel="Agregar"
          excludeIds={members.map((m) => m.id)}
          emptyHint="Ya esta todo el equipo en este proyecto."
          pending={addMember.isPending}
          error={addMember.error ? (addMember.error as Error).message : null}
          onClose={() => setAddingPeople(false)}
          onPick={(person) => addMember.mutate({ userId: person.id, role: 'DEVELOPER' })}
        />
      )}
    </div>
  );
}

function TaskCard({ item, onOpen }: { item: BacklogItem; onOpen: () => void }) {
  const doneCriteria = item.criteria.filter((c) => c.done).length;

  return (
    <article
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', item.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`${item.key}: ${item.title}`}
      className="flex cursor-pointer flex-col gap-[9px] rounded-card border border-line bg-surface p-3 shadow-[0_1px_2px_rgba(0,0,0,.3)] transition-all hover:border-line-hover hover:shadow-[0_6px_18px_rgba(0,0,0,.45)]"
    >
      <div className="flex items-center gap-1.5">
        <PriorityBadge priority={item.priority} dot />
        <TypeBadge type={item.type} />
        {item.isBlocked && <BlockedBadge />}
        <span className="key-badge ml-auto">{item.key}</span>
      </div>

      <h3 className="text-[13.5px] font-medium leading-[1.35] text-pretty">{item.title}</h3>

      {item.criteria.length > 0 && (
        <p className="text-[11.5px] text-faint">
          {doneCriteria}/{item.criteria.length} criterios
        </p>
      )}

      <div className="flex items-center gap-2">
        <Avatar user={item.assignee} size={24} />
        {item._count.comments > 0 && (
          <span className="text-[11.5px] text-faint">💬 {item._count.comments}</span>
        )}
        {item.storyPoints !== null && (
          <span className="points-badge ml-auto">{item.storyPoints}</span>
        )}
      </div>
    </article>
  );
}
